import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { filter as lodashFilter, find, map } from 'lodash'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import { useSidebarStore } from '@/features/sidebar'
import {
  CompanyList,
  DeleteReminderModal,
  EditReminderForm,
  ReminderItem,
  ReminderListSkeleton,
  SelectCompanyModal,
} from '@/features/workflow-config/components'
import { WorkflowSource } from '@/features/workflow-config/constants'
import {
  useAddCompaniesToWorkflow,
  useListCompanies,
  useUpdateWorkflowDefinition,
  useWorkflowDefinitionByCompany,
} from '@/features/workflow-config/hooks'
import { useWorkflowConfigStore } from '@/features/workflow-config/store'
import type {
  ReminderConfiguration,
  ReminderFormData,
} from '@/features/workflow-config/types'
import { useDebounce } from '@/hooks/useDebounce'
import { ReminderStatus, ThresholdUnit } from '@/schema'
import { cn } from '@/utils/common'

const WorkflowConfigPage: React.FC = () => {
  const { t } = useTranslation()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingReminder, setEditingReminder] =
    useState<ReminderConfiguration | null>(null)
  const [deletingReminder, setDeletingReminder] =
    useState<ReminderConfiguration | null>(null)
  const [updatingReminderId, setUpdatingReminderId] = useState<string | null>(
    null
  )
  const addFormRef = useRef<HTMLDivElement>(null)
  const contentContainerRef = useRef<HTMLDivElement>(null)

  const { isSidebarCollapsed } = useSidebarStore()

  const {
    selectedCompanyId,
    searchQuery,
    addConfigModalOpen,
    setSelectedCompanyId,
    setSearchQuery,
    openAddConfigModal,
    closeAddConfigModal,
  } = useWorkflowConfigStore()

  const debouncedSearchQuery = useDebounce(searchQuery)

  const {
    data: companiesData,
    isLoading: isLoadingCompanies,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useListCompanies(WorkflowSource.Configured, debouncedSearchQuery)

  const companies = useMemo(() => {
    if (!companiesData) return []
    return companiesData.pages.flatMap((page) => page.data)
  }, [companiesData])

  const addCompaniesToWorkflow = useAddCompaniesToWorkflow()
  const updateWorkflowDefinition = useUpdateWorkflowDefinition()

  useEffect(() => {
    if (companies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(companies[0].company_id)
    }
  }, [companies, selectedCompanyId, setSelectedCompanyId])

  const selectedCompany = find(companies, { company_id: selectedCompanyId }) as
    | (typeof companies)[0]
    | undefined

  // Fetch detailed workflow config using workflow_definition_id
  const workflowDefinitionId = selectedCompany?.workflow_definition_id || null
  const { data: workflowConfig, isLoading: isLoadingWorkflowConfig } =
    useWorkflowDefinitionByCompany(workflowDefinitionId)

  const reminders = useMemo(
    () => workflowConfig?.reminder_configurations || [],
    [workflowConfig?.reminder_configurations]
  )

  const handleAddReminderClick = () => {
    openAddConfigModal()
  }

  const handleShowAddForm = () => {
    setShowAddForm(true)
    setTimeout(() => {
      if (addFormRef.current && contentContainerRef.current) {
        addFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        })
      }
    }, 100)
  }

  const handleUpdateReminder = async (data: ReminderFormData) => {
    if (!selectedCompanyId || !workflowConfig?.id) return

    const newReminder: ReminderConfiguration = {
      name: data.name,
      status: data.status as ReminderStatus,
      unit: data.unit as ThresholdUnit,
      value: data.value,
    }

    const definitionId = workflowConfig.id

    const updatedConfigurations = [...reminders, newReminder]

    setShowAddForm(false)

    try {
      await updateWorkflowDefinition.mutateAsync({
        definitionId,
        companyId: selectedCompanyId,
        data: {
          reminder_configurations: updatedConfigurations,
        },
      })

      triggerToast('success', t('workflowConfig.toast.reminderAdded'))
    } catch {
      triggerToast('error', t('workflowConfig.toast.configUpdateFailed'))
      setShowAddForm(true)
    }
  }

  const handleInlineEditReminder = async (
    name: string,
    status: string,
    unit: string,
    value: number
  ) => {
    if (!selectedCompanyId || !workflowConfig?.id) return

    const definitionId = workflowConfig.id
    const reminderId = `${name}-${unit}-${value}`
    setUpdatingReminderId(reminderId)

    try {
      const updatedConfigurations = reminders.map((r) => {
        if (r.name === name && r.unit === unit && r.value === value) {
          return {
            ...r,
            status: status as ReminderStatus,
          }
        }
        return r
      })

      await updateWorkflowDefinition.mutateAsync({
        definitionId,
        companyId: selectedCompanyId,
        data: {
          reminder_configurations: updatedConfigurations,
        },
      })
      const toastMessage =
        status === ReminderStatus.Active
          ? t('workflowConfig.toast.reminderActivated')
          : t('workflowConfig.toast.reminderDeactivated')
      triggerToast('success', toastMessage)
    } catch {
      triggerToast('error', t('workflowConfig.toast.configUpdateFailed'))
    } finally {
      setUpdatingReminderId(null)
    }
  }

  const handleDeleteReminderClick = (reminder: ReminderConfiguration) => {
    setDeletingReminder(reminder)
  }

  const handleConfirmDeleteReminder = async () => {
    if (!deletingReminder || !selectedCompanyId || !workflowConfig?.id) {
      return
    }

    const definitionId = workflowConfig.id

    try {
      const updatedConfigurations = lodashFilter(
        reminders,
        (r) =>
          !(
            r.name === deletingReminder.name &&
            r.unit === deletingReminder.unit &&
            r.value === deletingReminder.value
          )
      )

      await updateWorkflowDefinition.mutateAsync({
        definitionId,
        companyId: selectedCompanyId,
        data: {
          reminder_configurations: updatedConfigurations,
        },
      })
      triggerToast('success', t('workflowConfig.toast.reminderDeleted'))
      setDeletingReminder(null)
    } catch {
      triggerToast('error', t('workflowConfig.toast.reminderDeleteFailed'))
    }
  }

  const handleEditReminderClick = (reminder: ReminderConfiguration) => {
    setEditingReminder(reminder)
  }

  const handleSaveEditedReminder = async (data: ReminderFormData) => {
    if (!editingReminder || !selectedCompanyId || !workflowConfig?.id) {
      return
    }

    const definitionId = workflowConfig.id

    try {
      const updatedReminder: ReminderConfiguration = {
        name: data.name,
        status: data.status as ReminderStatus,
        unit: data.unit as ThresholdUnit,
        value: data.value,
      }

      const updatedConfigurations = reminders.map((r) =>
        r.name === editingReminder.name &&
        r.unit === editingReminder.unit &&
        r.value === editingReminder.value
          ? updatedReminder
          : r
      )

      await updateWorkflowDefinition.mutateAsync({
        definitionId,
        companyId: selectedCompanyId,
        data: {
          reminder_configurations: updatedConfigurations,
        },
      })
      triggerToast('success', t('workflowConfig.toast.reminderUpdated'))
      setEditingReminder(null)
    } catch {
      triggerToast('error', t('workflowConfig.toast.reminderUpdateFailed'))
    }
  }

  const existingReminderNames = useMemo(
    () => map(reminders, 'name'),
    [reminders]
  )

  return (
    <>
      <div className="flex h-full">
        <div
          className={cn(
            'flex-shrink-0 transition-all duration-300',
            isSidebarCollapsed ? 'w-72' : 'w-80'
          )}
        >
          <CompanyList
            companies={companies}
            selectedCompanyId={selectedCompanyId}
            onSelectCompany={setSelectedCompanyId}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onAddReminder={handleAddReminderClick}
            isLoading={isLoadingCompanies}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            onLoadMore={() => fetchNextPage()}
          />
        </div>

        <div
          ref={contentContainerRef}
          className={cn('flex-1 overflow-y-auto transition-all duration-300')}
        >
          {selectedCompany ? (
            <div
              className={cn(
                'mx-8',
                isSidebarCollapsed ? 'max-w-4xl' : 'max-w-3xl'
              )}
            >
              <div className="mb-6">
                <h1 className="mb-0 text-2xl font-bold text-gray-light-900">
                  {selectedCompany.company_name}
                </h1>
                <p className="m-0 text-sm text-gray-light-600">
                  {t('workflowConfig.subtitle')}
                </p>
              </div>

              <div className="flex items-center justify-between mb-3">
                <h2 className="my-0 text-lg font-semibold text-gray-light-900">
                  {t('workflowConfig.reminders')}
                </h2>
              </div>
              {isLoadingWorkflowConfig ? (
                <ReminderListSkeleton count={4} />
              ) : (
                <>
                  <div className="space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
                    {reminders.map(
                      (reminder: ReminderConfiguration, index: number) => {
                        const isEditing =
                          editingReminder?.name === reminder.name &&
                          editingReminder?.unit === reminder.unit &&
                          editingReminder?.value === reminder.value

                        if (isEditing) {
                          return (
                            <div
                              key={`${reminder.name}-${reminder.unit}-${reminder.value}-${index}`}
                            >
                              <EditReminderForm
                                reminderName={reminder.name}
                                reminderStatus={reminder.status}
                                reminderUnit={reminder.unit}
                                reminderValue={reminder.value}
                                onSave={handleSaveEditedReminder}
                                onCancel={() => setEditingReminder(null)}
                                existingNames={existingReminderNames.filter(
                                  (n) => n !== reminder.name
                                )}
                                existingReminders={reminders.filter(
                                  (r: ReminderConfiguration) =>
                                    !(
                                      r.name === reminder.name &&
                                      r.value === reminder.value
                                    )
                                )}
                                isLoading={updateWorkflowDefinition.isPending}
                              />
                            </div>
                          )
                        }

                        return (
                          <ReminderItem
                            key={`${reminder.name}-${reminder.unit}-${reminder.value}-${index}`}
                            reminder={reminder}
                            onEdit={(name, status, unit, value) =>
                              handleInlineEditReminder(
                                name,
                                status,
                                unit,
                                value
                              )
                            }
                            onDelete={handleDeleteReminderClick}
                            onEditClick={handleEditReminderClick}
                            isLoading={
                              updatingReminderId ===
                              `${reminder.name}-${reminder.unit}-${reminder.value}`
                            }
                          />
                        )
                      }
                    )}
                  </div>

                  {!showAddForm && !editingReminder ? (
                    <Button
                      onClick={handleShowAddForm}
                      variant="outlined"
                      color="default"
                      className="mt-5"
                    >
                      {t('workflowConfig.addReminderButton')}
                    </Button>
                  ) : (
                    showAddForm &&
                    !editingReminder && (
                      <div ref={addFormRef} className="mt-4 mr-2">
                        <EditReminderForm
                          reminderName=""
                          reminderStatus={ReminderStatus.Active}
                          reminderUnit={ThresholdUnit.Days}
                          onSave={handleUpdateReminder}
                          onCancel={() => setShowAddForm(false)}
                          existingNames={existingReminderNames}
                          existingReminders={reminders}
                          isLoading={updateWorkflowDefinition.isPending}
                        />
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full" />
          )}
        </div>
      </div>

      <SelectCompanyModal
        open={addConfigModalOpen}
        onClose={closeAddConfigModal}
        onAdd={async (companyIds) => {
          if (companyIds.length > 0) {
            try {
              await addCompaniesToWorkflow.mutateAsync({
                company_ids: companyIds,
              })
              triggerToast('success', t('workflowConfig.toast.companiesAdded'))
              closeAddConfigModal()
            } catch {
              triggerToast(
                'error',
                t('workflowConfig.toast.companiesAddFailed')
              )
            }
          }
        }}
      />

      <DeleteReminderModal
        open={!!deletingReminder}
        onClose={() => setDeletingReminder(null)}
        reminderName={deletingReminder?.name || ''}
        onConfirm={handleConfirmDeleteReminder}
        isLoading={updateWorkflowDefinition.isPending}
      />
    </>
  )
}

export default WorkflowConfigPage
